/**
 * PATH: packages/email-hub/src/feature/email-layout/useEmailLayoutController.ts
 * FILE_ID: EMAIL-LAYOUT-CONTROLLER-001
 * PURPOSE: Compose EmailLayout's existing hooks and event handlers.
 * WHY: The public frame stays declarative while behavior remains hook-owned.
 */

import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import { useToast } from '../../_internal/components/Toast'
import { useUiStore } from '../../_internal/stores/uiStore'
import {
  QUOTECH_AUTH_LOST_EVENT,
} from '../../_internal/services/quotechApi'
import { useIntegrationEvents } from '../../_internal/services/integrationEvents'
import { seedFromIntent, useComposeDockStore } from '../compose/composeDockStore'
import {
  type EmailAccount,
  useEmailAccounts,
  useEmailStats,
  useEmailSyncStatus,
  useGmailOAuthResultToast,
  useGmailStatus,
  useOutlookOAuthResultToast,
  useOutlookStatus,
  useSyncEmails,
} from '../hooks/useEmails'
import { countDrafts, subscribeDraftChanges } from '../lib/draftStore'
import { emailApi } from '../lib/api'
import { isComposePath } from '../pages/inboxComposeMode'
import { useEmailSyncRuntime } from './useEmailSyncRuntime'
import { useLayoutPopovers } from './useLayoutPopovers'
import { useLayoutPreferences } from './useLayoutPreferences'
import { useMobileNavigation } from './useMobileNavigation'

export function useEmailLayoutController() {
  const location = useLocation()
  // /email/compose mounts ComposePage full-screen. Close every floating
  // compose window first so two ComposePages never race the same draft slot.
  useEffect(() => {
    if (isComposePath(location.pathname)) {
      useComposeDockStore.getState().closeAll()
    }
  }, [location.pathname])

  const { session } = useApiAuth()
  const { stats } = useEmailStats()
  const draftCount = useSyncExternalStore(
    subscribeDraftChanges,
    () => countDrafts(),
    () => 0,
  )
  const {
    sync,
    syncAccount,
    isSyncing,
    result: lastSyncResult,
  } = useSyncEmails()
  const outlook = useOutlookStatus()
  const gmail = useGmailStatus()
  const {
    connected: outlookConnected,
    authorize: connectOutlook,
    isExpired: outlookExpired,
  } = outlook
  const {
    accounts: emailAccounts,
    reload: reloadAccounts,
  } = useEmailAccounts()
  const orgHasEmailConnected = outlook.orgConnected || gmail.orgConnected
  const hasSharedMailboxAccess = emailAccounts.length > 0
  const { data: syncStatus } = useEmailSyncStatus(
    orgHasEmailConnected || hasSharedMailboxAccess,
  )
  const handleSyncAccount = useCallback(
    async (accountId: number): Promise<void> => {
      await syncAccount(accountId, 30)
    },
    [syncAccount],
  )
  const openSettings = useUiStore(state => state.openSettings)
  const handleReconnectMailbox = useCallback(
    (account: EmailAccount) => {
      if (account.provider === 'gmail') {
        void gmail.authorize(account.email_address)
      } else {
        void connectOutlook(account.email_address)
      }
    },
    [gmail, connectOutlook],
  )

  const preferences = useLayoutPreferences(session)
  const { toast } = useToast()
  const handleSetMailboxPrivacy = useCallback(
    async (accountId: number, makePrivate: boolean): Promise<void> => {
      try {
        await emailApi(`/api/email-accounts/${accountId}/`, {
          method: 'PATCH',
          body: JSON.stringify({
            account_type: makePrivate ? 'personal' : 'shared',
          }),
        })
        await reloadAccounts()
        toast(
          makePrivate
            ? 'Mailbox is now private. Only you can see its emails.'
            : 'Mailbox is now shared with your organisation.',
          'success',
        )
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Could not update mailbox privacy'
        toast(message, 'error')
      }
    },
    [reloadAccounts, toast],
  )

  const mobile = useMobileNavigation(
    location.pathname,
    preferences.navPosition,
    preferences.userCollapsed,
  )

  useOutlookOAuthResultToast(toast, outlook.check)
  useGmailOAuthResultToast(toast, gmail.check)

  const [issuesOpen, setIssuesOpen] = useState(false)
  const {
    errorCount: issueErrorCount,
    warningCount: issueWarningCount,
  } = useIntegrationEvents({ limit: 50, pollMs: 60_000 })
  const [addMailboxPickerOpen, setAddMailboxPickerOpen] = useState(false)

  // The host session and the Quotech master session are distinct. Short-
  // circuit routed email pages when the master cookie is lost.
  const [quotechAuthLost, setQuotechAuthLost] = useState(false)
  useEffect(() => {
    const onAuthLost = () => setQuotechAuthLost(true)
    window.addEventListener(QUOTECH_AUTH_LOST_EVENT, onAuthLost)
    return () => window.removeEventListener(QUOTECH_AUTH_LOST_EVENT, onAuthLost)
  }, [])

  const popovers = useLayoutPopovers()
  const syncRuntime = useEmailSyncRuntime({
    session,
    sync,
    isSyncing,
    syncStatus,
    outlook,
    outlookConnected,
    outlookExpired,
    toast,
  })

  return {
    session,
    stats,
    draftCount,
    isSyncing,
    lastSyncResult,
    outlook,
    gmail,
    outlookConnected,
    connectOutlook,
    outlookExpired,
    emailAccounts,
    syncStatus,
    handleSyncAccount,
    openSettings,
    handleReconnectMailbox,
    hasSharedMailboxAccess,
    handleSetMailboxPrivacy,
    issuesOpen,
    setIssuesOpen,
    issueErrorCount,
    issueWarningCount,
    addMailboxPickerOpen,
    setAddMailboxPickerOpen,
    quotechAuthLost,
    openNewCompose: () => {
      useComposeDockStore
        .getState()
        .openOrRestore(seedFromIntent('new', null))
    },
    ...preferences,
    ...mobile,
    ...popovers,
    ...syncRuntime,
  }
}

export type EmailLayoutController = ReturnType<typeof useEmailLayoutController>
